/* eslint-disable no-undef */
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { dataDogInit } from 'helpers/dataDog';
import qs from 'query-string';

import {
  AccountManager,
  AnalyticsManager,
  ApiManager,
  Atlas,
  BetManager,
  Bus,
  DetectionProvider,
  ExtensionsManager,
  Fuse,
  HistoryManager,
  initDebug,
  isNotEmptyObject,
  Logger,
  NotificationManager,
  SettingsManager,
  SiteConfigManager,
  SportManager,
  StaticBlocksProvider,
  SubscriptionManager,
  TranslationProvider,
  UserMessagesProvider,
} from '@atlas/core';
import { COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { Messages, TransportManager } from '@atlas/transport';
import { Button, EmptyStateView, Loader } from '@atlas/ui';

import Listen from 'common/Affiltate/init';
import network from 'common/Affiltate/Manager';
import Notifications from 'common/Notifications';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'common/utils';
import history from 'components/Layout/history';
import Layout from 'components/Layout/Layout';
import LiveChatDeepLinkMount from 'components/LiveChat/LiveChatDeepLinkMount';
import MainRouter from 'components/MainRouter/MainRouter';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import SeoPageProvider from 'components/SeoPage/SeoPageProvider';
import ToastContainer from 'components/Toast/ToastContainer';
import BonusesProvider from 'src/components/BonusesProvider';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';
import SelfRegulationProvider from 'src/components/SelfRegulationProvider';
import { OddsFormatProvider } from 'src/iframe/components/IFrameBetslip/OddsFormatProvider';

import 'common/Form/formConfig';
import '../subscriptions/dataLayer.bindings';

import packageData from '../../package.json';
import { resolveFingerprint } from '../fingerprint';
import busEventsMapping from '../subscriptions/bus.bindings';
import subscribeUserOffline from '../subscriptions/subscribeUserOffline';

// Some casino providers (e.g. Playtech) navigate their game iframe to the lobbyUrl we pass, which
// loads the site inside the game frame ("lobby in lobby"). The site build is never meant to run
// framed — the dedicated iframe build sets window.IS_IFRAME — so break out to the top window.
const isFramedSiteBuild = window.top && window.top !== window.self && !window.IS_IFRAME;

if (isFramedSiteBuild) {
  try {
    window.top.location.replace(window.location.pathname);
  } catch (error) {
    Logger.debug('iframe break-out skipped', error);
  }
}

dataDogInit();
initDebug();

const ShareCss = React.lazy(() => import('modules/share-css'));

const ResultsWrapper = React.lazy(() => import('components/Sportsbook/ResultsWrapper'));
const ClientProfilePage = React.lazy(() => import('components/ClientProfile'));
const Sportsbook = React.lazy(() => import('components/Sportsbook/Sportsbook'));

const Casino = React.lazy(() => import('components/Casino'));
const CasinoHome = React.lazy(() => import('components/Casino/CasinoHome/CasinoHome'));
const VirtualGame = React.lazy(() => import('components/VirtualGame/VirtualGame'));
const TVGames = React.lazy(() => import('components/Casino/TVGamesPage'));

const PromotionsRouter = React.lazy(() => import('components/Promotion/PromotionsRouter'));
const BlogRouter = React.lazy(() => import('components/Blog/BlogRouter'));
const TournamentsRouter = React.lazy(() => import('components/Tournaments/TournamentsRouter'));
const SmarticoTournamentsRouter = React.lazy(
  () => import('components/Tournaments/Smartico/TournamentsRouter'),
);
const LuckyDay = React.lazy(() => import('components/LuckyDay/LuckyDay'));
const CmsPageList = React.lazy(() => import('components/CmsPageList/CmsPageList'));
const AllProviders = React.lazy(() => import('components/AllProviders/AllProviders'));
import {
  syncRequestBuilderAuthFromApiManager,
  syncRequestBuilderDefaultHeaders,
} from 'common/utils/requestBuilder';
const ComponentLoadingFallback = <Loader className="viewport-height" />;

const ComponentLoadingError = () => (
  <EmptyStateView
    className="viewport-height"
    description="page_loading_error"
    title="page_loading_error_title"
  >
    <Button
      className="block"
      label="refresh"
      onClick={() => window.location.reload()}
      type="primary"
    />
  </EmptyStateView>
);

const SuspenseComponent = Component =>
  function () {
    return (
      <Fuse fallback={ComponentLoadingError}>
        <Suspense fallback={ComponentLoadingFallback}>
          <Component />
        </Suspense>
      </Fuse>
    );
  };

const SuspenseCssComponent = () => (
  <Suspense fallback={<span style={{ display: 'none' }} />}>
    <ShareCss />
  </Suspense>
);

// Updating application default settings using local config
const localLanguage = LocalConfig.get('language');
if (localLanguage) {
  SettingsManager.settings.lang = localLanguage;
}

const {
  allProvidersPage: allProvidersDefaultPath,
  blog: blogDefaultPath,
  casino: casinoDefaultPath,
  casinoHome: casinoHomeDefaultPath,
  clientProfile: accountDefaultPath,
  live: sportLiveDefaultPath,
  liveCasino: liveCasinoDefaultPath,
  luckyday: luckydayDefaultPath,
  promotionTournaments: promotionTournamentsDefaultPath,
  promotions: promotionsDefaultPath,
  results: resultsDefaultPath,
  sportsbook: sportPrematchDefaultPath,
  tournaments: tournamentsDefaultPath,
  tvGames: tvGamesDefaultPath,
  virtualGame: virtualGameDefaultPath,
} = staticRoutes;

const {
  All_PROVIDERS,
  BLOG,
  CASINO,
  CASINO_LIVE,
  CLIENT_PROFILE,
  LUCKY_DAY,
  PROMOTION_TOURNAMENTS,
  PROMOTIONS,
  RESULTS,
  SPORT_LIVE,
  SPORT_PREMATCH,
  TOURNAMENTS,
  TV_GAMES,
  VIRTUAL_GAME,
} = COMPONENT_TYPES;

SiteConfigManager.setLayoutConfig({
  [All_PROVIDERS]: {
    component: SuspenseComponent(AllProviders),
    defaultPath: allProvidersDefaultPath,
  },
  [BLOG]: {
    component: SuspenseComponent(BlogRouter),
    defaultPath: blogDefaultPath,
    optionalPath: '/:id?',
  },
  [CASINO]: {
    component: SuspenseComponent(Casino),
    defaultPath: casinoDefaultPath,
  },
  [CASINO_LIVE]: {
    component: SuspenseComponent(Casino),
    defaultPath: liveCasinoDefaultPath,
  },
  [CLIENT_PROFILE]: {
    component: SuspenseComponent(ClientProfilePage),
    defaultPath: accountDefaultPath,
  },
  [LUCKY_DAY]: {
    component: SuspenseComponent(LuckyDay),
    defaultPath: luckydayDefaultPath,
  },
  [PROMOTIONS]: {
    component: SuspenseComponent(PromotionsRouter),
    defaultPath: promotionsDefaultPath,
    optionalPath: '/:id?',
  },
  [PROMOTION_TOURNAMENTS]: {
    component: SuspenseComponent(TournamentsRouter),
    defaultPath: promotionTournamentsDefaultPath,
    optionalPath: '/:id?',
  },
  [RESULTS]: {
    component: SuspenseComponent(ResultsWrapper),
    defaultPath: resultsDefaultPath,
  },
  [SPORT_LIVE]: {
    component: SuspenseComponent(Sportsbook),
    defaultPath: sportLiveDefaultPath,
  },
  [SPORT_PREMATCH]: {
    component: SuspenseComponent(Sportsbook),
    defaultPath: sportPrematchDefaultPath,
  },
  [TOURNAMENTS]: {
    component: SuspenseComponent(SmarticoTournamentsRouter),
    defaultPath: tournamentsDefaultPath,
    optionalPath: '/:id?',
  },
  [TV_GAMES]: {
    component: SuspenseComponent(TVGames),
    defaultPath: tvGamesDefaultPath,
    optionalPath: '/:gameId?',
  },
  [VIRTUAL_GAME]: {
    component: SuspenseComponent(VirtualGame),
    path: virtualGameDefaultPath,
  },
  casinoHome: {
    component: SuspenseComponent(CasinoHome),
    defaultPath: casinoHomeDefaultPath,
    optionalPath: '/:gameId?',
  },
  pages: {
    component: SuspenseComponent(CmsPageList),
    defaultPath: '/pages',
  },
});

const providedQueryParams = qs.parse(window.location.search);

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line import/extensions, global-require
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

const betslipCMSConfig = SiteConfigManager.getConfig('config.config.betslip');
if (betslipCMSConfig) {
  BetManager.updateBetslipSettings(betslipCMSConfig);
}

const defaultAcceptOddChanges = SiteConfigManager.getConfig(
  'config.config.betslip.defaultAcceptOddsChangesType',
);
if (defaultAcceptOddChanges) {
  SettingsManager.settings.acceptOddsChanges = defaultAcceptOddChanges;
}

const defaultOddsZeroes = SiteConfigManager.getConfig('config.config.odds_zeroes');
if (defaultOddsZeroes) {
  SettingsManager.settings.oddsZeroes = defaultOddsZeroes;
}

const defaultOddsFractionDigits = SiteConfigManager.getConfig('config.config.odds_fraction_digits');
if (defaultOddsFractionDigits) {
  SettingsManager.settings.oddsFractionDigits = defaultOddsFractionDigits;
}

const cnf = SiteConfigManager.getConfig('config.config.registry');

if (cnf) {
  network.setConfig(cnf);
  Listen();
}

// Resolving stored settings
SettingsManager.resolveSettings();

busEventsMapping(Bus, history);
subscribeUserOffline();

// Initializing api default headers
const defaultHeaders = {
  device: 'desktop',
  version: packageData.version,
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

SiteConfigManager.device = 'desktop';
// Setting application version & device type
ApiManager.setDefaultHeaders(defaultHeaders);
syncRequestBuilderDefaultHeaders(defaultHeaders);
syncRequestBuilderAuthFromApiManager(ApiManager);

syncRequestBuilderDefaultHeaders(defaultHeaders);
syncRequestBuilderAuthFromApiManager(ApiManager);

const { host, protocol } = window.location;
const webSocketProtocol = protocol.startsWith('https') ? 'wss:' : 'ws:';

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('ws', `${webSocketProtocol}//${host}/ws/notifier2`);
ApiManager.setHostname('oddshub', `${webSocketProtocol}//${host}/odds-hub/ws`);
ApiManager.setHostname('media', 'http://app4.front-server.atlas-iac.com');
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

if (process.env.NODE_ENV === 'development') {
  window.BetManager = BetManager;
  window.Bus = Bus;

  window.AccountManager = AccountManager;
  window.SportManager = SportManager;
  window.NotificationManager = NotificationManager;
  window.SiteConfigManager = SiteConfigManager;
  window.HistoryManager = HistoryManager;
  window.TransportManager = TransportManager.getInstance();
  window.SubscriptionManager = SubscriptionManager;
  window.SettingsManager = SettingsManager;
}

if (providedQueryParams.token) {
  ApiManager.setAuthorizationToken(providedQueryParams.token);
  AccountManager.setToken(providedQueryParams.token);
}

SubscriptionManager.authorizationSubscription = Messages.authorizedClientUpdates();

// Creating atlas extensions manager
window.aem = new ExtensionsManager();
// Moving affiltate manager to global scope

const wsUrl = new URL(ApiManager.getHostname('ws'));
wsUrl.searchParams.set('projectId', projectId);
if (process.env.NODE_ENV === 'development') {
  // Added for security reason, special WAF rule created on Cloudflare
  wsUrl.searchParams.set('project-team-frontend-team', 'x');
}
const transport = TransportManager.getInstance();
transport.initialize(wsUrl);
transport.setOptions({
  projectId,
});

// Set Auth Token for API service
ApiManager.setAuthorizationToken(AccountManager.getToken());

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      <TranslationProvider>
        <GlobalMaintenanceContainer>
          <UserMessagesProvider>
            <StaticBlocksProvider>
              <DetectionProvider>
                <LayoutLoader>
                  <Layout>
                    <SeoPageProvider>
                      <SelfRegulationProvider>
                        <BonusesProvider>
                          <OddsFormatProvider>
                            <MainRouter />
                          </OddsFormatProvider>
                        </BonusesProvider>
                      </SelfRegulationProvider>
                    </SeoPageProvider>
                  </Layout>
                </LayoutLoader>
              </DetectionProvider>
              <Notifications />
              <ToastContainer />
              <LiveChatDeepLinkMount />
            </StaticBlocksProvider>
          </UserMessagesProvider>
        </GlobalMaintenanceContainer>
      </TranslationProvider>
    </Atlas>
  </>,
);
